import React, { useEffect, useState } from "react";
import { Box, IconButton, Popover, Tooltip, Typography } from "@mui/material";
import { TextFields, ZoomIn, ZoomOut } from "@mui/icons-material";
import PropTypes from "prop-types";
import { getPreference, setPreference } from "../../services/cookie-service";

const FONTSIZE = "fontSize";
const DEFAULT_FONTSIZE = 16;

const FontSizeControl = ({ onFontSizeChange }) => {
  const [currentSize, setCurrentSize] = useState(DEFAULT_FONTSIZE);
  const [anchorEl, setAnchorEl] = useState(null);

  // Initialize font size from localStorage or default to 16px
  useEffect(() => {
    const savedSize = getPreference(FONTSIZE, DEFAULT_FONTSIZE);
    if (savedSize) {
      const size = parseFloat(savedSize);
      document.documentElement.style.fontSize = `${size}px`;
      setCurrentSize(size);
      onFontSizeChange?.(size);
    }
  }, [onFontSizeChange]);
  
  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleZoomIn = () => {
    const root = document.documentElement;
    const newSize = Math.min(currentSize + 2, 24); // Max size 24px
    root.style.fontSize = `${newSize}px`;
    localStorage.setItem("fontSize", newSize.toString());
    setCurrentSize(newSize);
    onFontSizeChange?.(newSize);
    setPreference(FONTSIZE, newSize);
  };

  const handleZoomOut = () => {
    const root = document.documentElement;
    const newSize = Math.max(currentSize - 2, 12); // Min size 12px
    root.style.fontSize = `${newSize}px`;
    localStorage.setItem("fontSize", newSize.toString());
    setCurrentSize(newSize);
    onFontSizeChange?.(newSize);
    setPreference(FONTSIZE, newSize);
  };

  // Calculate percentage relative to base size (16px)
  const percentage = Math.round((currentSize / 16) * 100);
  const open = Boolean(anchorEl);

  return (
    <>
      {/* Floating button trigger */}
      <Tooltip title="Font Size">
        <IconButton color="inherit" onClick={handleOpen}>
          <TextFields />
        </IconButton>
      </Tooltip>

      {/* Popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        disableScrollLock={true} // ⬅️ this disables both overflow:hidden and padding-right
        keepMounted={true}
      >
        <Box
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexDirection: "column",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Tooltip title="Decrease font size">
              <IconButton
                onClick={handleZoomOut}
                size="small"
                aria-label="Decrease font size"
              >
                <ZoomOut />
              </IconButton>
            </Tooltip>

            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {percentage}%
            </Typography>

            <Tooltip title="Increase font size">
              <IconButton
                onClick={handleZoomIn}
                size="small"
                aria-label="Increase font size"
              >
                <ZoomIn />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

FontSizeControl.propTypes = {
  onFontSizeChange: PropTypes.func,
};

export default FontSizeControl;
